<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Affiliates..."
    button-location="top"
    @submit="loadAffiliates()"
  >
    <template #title>
      Affiliate List
    </template>

    <template #top-form>
      <!-- only admin can create affiliate -->
      <div
        v-if="isAvailableToCreateOrUpdate"
        class="row"
      >
        <p-button
          type="purple"
          class="ml-20"
          round
          @click="onCreateAffiliate"
        >
          Create New Affiliate
        </p-button>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Filter Key </label>
        <div class="col-sm-5">
          <sanitized-input
            id="searchKey"
            v-model="status.query"
            placeholder="Please input filter key"
          />
        </div>
        <div class="col-sm-3 form-submit-button-group left-button-group">
          <p-button
            type="purple"
            round
            native-type="submit"
            :disabled="status.isLoading"
          >
            Load Affiliates
          </p-button>

          <p-button
            type="yellow"
            round
            :disabled="status.isLoading"
            @click="resetForm"
          >
            Reset
          </p-button>
        </div>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="paginationInfo.currentPage"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="loadAffiliates"
        />
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="affiliates"
        :table-columns="tableColumns"
      >
        <template #right-fixed-columns>
          <!-- Show edit icon for only admin -->
          <el-table-column
            v-if="!!affiliates.length && isAvailableToCreateOrUpdate"
            fixed="right"
            label="Edit"
            width="50"
          >
            <template #default="scope">
              <div
                class="info-icon"
                @click="onUpdateAffiliate(scope.row)"
              >
                <i class="fa fa-edit" />
              </div>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, toRefs } from 'vue';
import { useRouter } from 'vue-router';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { useProfile } from '@/use/profile';
import { TableColumnType } from '@/models/table';
import { Affiliate } from '@/models/affiliate';
import { affiliateService } from '@/services/affiliate';

const pageId = 'affiliate-table';
const tablePaginationLimit = 30;
const tableColumns: TableColumnType[] = [
  { field: 'index', label: 'No', width: 60, isDisableSort: true, isCenterAlign: true },
  { field: 'name', label: 'Name', width: 150 },
  { field: 'codeName', label: 'Code', width: 150 },
  { field: 'extension', label: 'Extension', width: 150 },
  { field: 'numberOfCallers', label: 'Number of Callers', width: 150 },
  { field: 'websiteRegistration', label: 'URL', width: 200 },
  { field: 'description', label: 'Description', minWidth: 300 },
];

const router = useRouter();
const { userRoles } = useProfile();
const { isAdmin, isAffiliateManager } = toRefs(userRoles);
const { showErrorPopupDialog } = useSwalDialog();

const affiliates: Ref<Affiliate[]> = ref([]);
const status = reactive({
  isLoading: false,
  query: '',
});
const paginationInfo = reactive({
  currentPage: 1,
  totalPages: 1,
});

const isAvailableToCreateOrUpdate = computed(() => isAdmin.value || isAffiliateManager.value);

const loadAffiliates = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * tablePaginationLimit;
  paginationInfo.currentPage = currentPage;
  status.isLoading = true;

  try {
    const response = await affiliateService.fetchAffiliates({
      isDisabled: false,
      search: status.query,
      pagination: {
        limit: tablePaginationLimit,
        offset,
      },
    });

    paginationInfo.totalPages = Math.ceil(response.count / tablePaginationLimit) || 1;

    affiliates.value = response.rows.map((item: Affiliate, index: number) => {
      item.index = offset + index + 1;

      const websiteRegistration = `https://gogograndparent.com/affiliates/${item.codeName}`;
      item.websiteRegistration = `<a href="${websiteRegistration}" target="_blank">${websiteRegistration}</a>`;

      return item;
    });
  } catch (error) {
    affiliates.value = [];
    paginationInfo.currentPage = 1;
    paginationInfo.totalPages = 1;

    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  status.isLoading = false;
};

const onCreateAffiliate = () => {
  router.push({
    name: 'CreateAffiliate',
  });
};

const onUpdateAffiliate = (row: Affiliate) => {
  router.push({
    params: {
      codeName: row.codeName,
    },
    name: 'UpdateAffiliate',
  });
};

const resetForm = () => {
  affiliates.value = [];

  paginationInfo.currentPage = 1;
  paginationInfo.totalPages = 1;

  status.query = '';
};
</script>
